/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-param-reassign */

import React from 'react';
import styled, { css } from 'styled-components';
import Content from 'TMComponents/Content';
import ImageDynamic from 'TMComponents/ImageDynamic';
import imageProps from 'TMPropTypes/imageProps';
import { string, array, shape, oneOf, node } from 'prop-types';
import cardBackgroundOption from 'TMComponents/GlobalStyle/cardBackgroundStyles';
import commonOptionsPropType from 'TMPropTypes/commonOptionsProps';
import ComponentMapper from 'TMComponents/ComponentMapper';
import { breakpoints } from 'TMConstants';
import NavLink from 'TMComponents/NavLink';

const baseCardStyles = css`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 350px;
    ${({ sizing }) => sizing && `width: ${sizing};`}

    max-width: 100%;
    min-height: 100%;
    box-sizing: border-box;
    border-radius: 32px;
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[9]};
`;

const CardBackground = styled.div`
    ${baseCardStyles}
    ${cardBackgroundOption}

    padding: 32px 20px 0 20px;
    margin: 0 20px;

    @media (max-width: ${breakpoints.mobileTablet}px) {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
        padding: 32px 24px 0 24px;

        &:not(:last-child) {
            margin-bottom: 20px;
        }
    }

    ${({ hasImage }) =>
        !hasImage &&
        `
        padding-bottom: 32px;
        @media (max-width: ${breakpoints.mobileTablet}px) {
            padding-bottom: 32px;
        }
    `}
    ${({ verticalPadding }) =>
        verticalPadding === 'small' && `padding: 12px 20px;`}

    overflow: hidden;
`;

const Corner = styled.div`
    position: absolute;
    margin: 40px 20px;

    right: 0;
    top: 0;
`;

const StyledContent = styled(Content)`
    display: flex;
    flex-direction: column;
    flex: 1;
    /* This finds the parent (SharedOptions wrapper) of 
    DynamicSeparator component  */
    *:has(.dynamic-separator) {
        margin-top: auto;
    }
`;

const Image = styled(ImageDynamic)`
    margin: 0 -20px 0px;
`;

const StyledLink = styled(NavLink)`
    text-decoration: none !important;

    @media (max-width: ${breakpoints.mobileTablet}px) {
        width: 100%;
    }
`;

const LinkDecorator = ({ href, children }) => {
    return href ? (
        <StyledLink href={href}>{children}</StyledLink>
    ) : (
        children
    );
};

LinkDecorator.propTypes = {
    children: node.isRequired,
    href: string
};

const LargeCard = ({
    image,
    content,
    commonOptions,
    sizing,
    verticalPadding = 'default',
    topRightCorner = [],
    href
}) => {
    // Auto-enable leftAlign if any button explicitly has left alignment
    const hasLeftAlignedButton = content?.some(
        (item) =>
            item.__typename === 'DatoCmsButton' &&
            item.alignment === 'left'
    );

    return (
        <LinkDecorator href={href}>
            <CardBackground
                cardBackgroundColor={commonOptions?.cardBackgroundColor}
                sizing={sizing}
                hasImage={!!image}
                verticalPadding={verticalPadding}>
                {topRightCorner[0] && (
                    <Corner>
                        <ComponentMapper {...topRightCorner[0]} />
                    </Corner>
                )}
                <StyledContent
                    noSidePadding
                    leftAlign={hasLeftAlignedButton}
                    content={content}
                />
                {image && (
                    <Image imageData={image} objectPosition="0% 50%" />
                )}
            </CardBackground>
        </LinkDecorator>
    );
};

export default LargeCard;

LargeCard.propTypes = {
    content: array.isRequired,
    sizing: string,
    href: string,
    verticalPadding: oneOf(['default', 'small']),
    image: shape(imageProps),

    ...commonOptionsPropType
};
